import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Button } from '@/shared/ui/button';
import { Loader2 } from 'lucide-react';
import { useUpdatePublicIp } from '@/domains/service-lines/services/account-service-lines';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';

interface PublicIpDialogProps {
  open: boolean;
  serviceLine: ServiceLine;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const PublicIpDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine,
}: PublicIpDialogProps) => {
  const [publicIp, setPublicIp] = useState(serviceLine?.publicIp ?? false);
  const updatePublicIp = useUpdatePublicIp();

  // Reset form state when dialog closes
  useEffect(() => {
    if (!open) {
      setPublicIp(serviceLine?.publicIp ?? false);
    }
  }, [open, serviceLine?.publicIp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updatePublicIp.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        publicIp: publicIp,
      });
      showSuccess(SUCCESS.SERVICE_LINE.UPDATED_PUBLIC_IP);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.UPDATE_PUBLIC_IP);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Public IP Settings</DialogTitle>
          <DialogDescription>
            Configure public IP settings for this service line.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="public-ip">Enable Public IP</Label>
              <p className="text-sm">
                When enabled, this service line will use a public IP address.
              </p>
            </div>
            <Switch
              id="public-ip"
              checked={publicIp}
              onCheckedChange={setPublicIp}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updatePublicIp.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updatePublicIp.isPending}>
              {updatePublicIp.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

